import * as React from "react";
import { StyleSheet, View } from "react-native";

import { Panel, Skeleton, Typography, VStack } from "@smartrent/ui";

import { formatCurrency } from "@/lib/helpers";

interface NetsuiteOrderGrandTotalProps {
  isLoading?: boolean;
  subTotal?: number;
  taxTotal?: number;
  shippingCost?: number;
  orderTotal?: number;
  shippingMethodLabel?: string;
  error?: string;
}

export const NetsuiteOrderGrandTotal: React.FC<
  NetsuiteOrderGrandTotalProps
> = ({
  isLoading,
  subTotal,
  taxTotal,
  shippingCost,
  orderTotal,
  shippingMethodLabel,
  error,
}) => {
  if (isLoading) {
    return (
      <Panel>
        <View style={styles.container}>
          <VStack spacing={12}>
            <Skeleton height={24} />
            <Skeleton height={24} />
            <Skeleton height={24} />
            <View style={styles.divider} />
            <Skeleton height={32} />
          </VStack>
        </View>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <View style={styles.container}>
          <Typography type="body">
            Error calculating order total. Please review your order and try
            again.
          </Typography>
        </View>
      </Panel>
    );
  }

  if (
    subTotal === undefined ||
    taxTotal === undefined ||
    shippingCost === undefined ||
    orderTotal === undefined
  ) {
    return (
      <Panel>
        <View style={styles.container}>
          <Typography type="body">
            Tax and shipping calculated at checkout
          </Typography>
        </View>
      </Panel>
    );
  }

  return (
    <Panel>
      <View style={styles.container}>
        <VStack spacing={12}>
          <View style={styles.row}>
            <Typography type="body">Subtotal</Typography>
            <Typography type="body">{formatCurrency(subTotal)}</Typography>
          </View>

          <View style={styles.row}>
            <Typography type="body">Tax</Typography>
            <Typography type="body">{formatCurrency(taxTotal)}</Typography>
          </View>

          <View style={styles.row}>
            <Typography type="body">
              {shippingMethodLabel
                ? `Estimated Shipping - ${shippingMethodLabel}`
                : "Estimated Shipping"}
            </Typography>
            <Typography type="body">{formatCurrency(shippingCost)}</Typography>
          </View>

          <View style={styles.divider} />

          <View style={styles.row}>
            <Typography type="title3">Grand Total</Typography>
            <Typography type="title3">{formatCurrency(orderTotal)}</Typography>
          </View>
        </VStack>
      </View>
    </Panel>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  divider: {
    height: 1,
    backgroundColor: "#e0e0e0",
    marginVertical: 8,
  },
});
